import type { LogSet } from "./schemas/logSet.js";
import type { Session } from "./schemas/session.js";
import type {
  ExerciseResult,
  SessionResult,
  VolumeByMuscle,
} from "./schemas/sessionResult.js";
import { computeE1rmKg } from "./pr.js";
import { compareWithPriors } from "./sessionResultPriors.js";
import { EQUIPMENT_INSTANCE_NS, readTagValue } from "./schemas/tag.js";
import { loadTrackKey } from "./e1rmPoints.js";
import { sessionDurationMin } from "./duration.js";
import { EFFORT_HARD_SET_RIR, effectiveRir } from "./effortScale.js";
import {
  countsFromMetrics,
  setLoadsBodyweight,
  setMetrics,
  type SetLoadLookup,
} from "./setMetrics.js";
import {
  muscleShortLabel,
  type CatalogMuscleSlug,
  type MuscleContrib,
} from "./muscleCatalog.js";
import {
  aggregateVolumeByMuscle,
  type VolumeBySetInput,
} from "./volumeByMuscle.js";

// Per-exercise minimal lookup the compute function needs from the
// catalog. Caller resolves catalog rows upstream (with the same per-user
// stock + draft fallback the rest of the API uses) and passes a Map.
// `muscleContrib` drives the volume-by-muscle aggregation (slice #4);
// `primary` survives as the back-compat fallback for catalog rows that
// pre-date the Phase 3 enrichment (cardio + any future custom row).
export type SessionResultExerciseLookup = SetLoadLookup & {
  name: string;
  primary?: readonly string[];
  secondary?: readonly string[];
  muscleContrib?: MuscleContrib;
};

// Per-exercise prior-history summary. The orchestration layer reads
// each SessionResult row for sessions with startedAt < this session's
// startedAt that contain the same exerciseSlug and reshapes them to
// this minimal record. Sticky-PR semantics: history must be strictly
// older than the session being computed, otherwise the function would
// compare the session against itself.
export type SessionResultPriorRecord = {
  sessionId: string;
  startedAt: string;
  topE1rmKg: number | null;
  topSetReps: number | null;
  totalVolumeKg: number;
  setCount: number;
};

export type SessionResultInput = {
  session: Session;
  setsInSession: readonly LogSet[];
  // Map keyed by exerciseSlug. Missing entries → exercise unknown; the
  // compute function still produces an ExerciseResult using the LogSet's
  // exerciseName snapshot and no muscle-group attribution.
  exerciseCatalog: ReadonlyMap<string, SessionResultExerciseLookup>;
  // Map keyed by exerciseSlug. Empty array (or missing entry) is allowed
  // — first-ever logging of that exercise sets isPr flags appropriately
  // and deltas to null.
  priorHistory: ReadonlyMap<string, ReadonlyArray<SessionResultPriorRecord>>;
  // Profile.bodyweightKg at compute time. Used to attribute volume to
  // kind=bodyweight sets via setVolumeKg. Absent / 0 → bodyweight sets
  // still contribute 0 (back-compat with sessions computed before the
  // bw-volume feature shipped). Historical re-derives use the current
  // profile bw as a proxy; per-set bw snapshots are a future enhancement.
  profileBodyweightKg?: number;
};

// Derive a short "QUADRICEPS · HAMSTRINGS"-style label from the top
// muscle groups by volume in this session. Empty volume map → "SESSION".
// Stored on the SessionResult row at compute time so the post-session
// card can render the header without re-aggregating. EN-only by design
// (stored derived string; user-facing live labels go through
// t(muscleKey(slug)) at render time).
const deriveSessionLabel = (
  volumeByMuscle: Partial<Record<CatalogMuscleSlug, number>>,
): string => {
  const entries = Object.entries(volumeByMuscle).filter(
    ([, v]) => typeof v === "number" && v > 0,
  ) as Array<[CatalogMuscleSlug, number]>;
  if (entries.length === 0) return "SESSION";
  entries.sort((a, b) => b[1] - a[1]);
  const top = entries.slice(0, 2).map(([slug]) => muscleShortLabel(slug));
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const label of top) {
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  }
  if (labels.length === 0) return "SESSION";
  return labels.join(" · ");
};

// Pure compute. No I/O, no clock other than the explicit computedAt arg.
// Returns the SessionResult ready to be PUT under the session's user
// partition. Caller owns the DDB write + the prior-history fetch.
//
// The function trusts the inputs: setsInSession is assumed to belong
// to session.id, priorHistory entries are assumed strictly older than
// session.startedAt. The orchestration layer (apps/api/src/routes/
// sessionResults.ts) enforces both invariants when it builds the input.
/**
 * Serie dure di una sessione intera: la somma degli `hardSetCount` dei suoi
 * esercizi. ⛔ Il predicato «dura» NON si ri-deriva qui — `hardSetCount` è già
 * calcolato in `computeSessionResult` filtrando su `effectiveRir(set) <=
 * EFFORT_HARD_SET_RIR` (con il fail che vale RIR 0), ed è persistito nello
 * schema: qui si somma soltanto.
 *
 * ⭐ Esiste perché i DUE host di Home (mobile `PlanScreen`, web-rnw
 * `useHomeSignals`) devono darla identica a `computeFormBadge` (§8ter): due
 * copie della stessa somma sono due copie che possono divergere.
 */
export function hardSetsOfResult(result: {
  exerciseResults: ReadonlyArray<{ hardSetCount?: number }>;
}): number {
  let n = 0;
  for (const e of result.exerciseResults) n += e.hardSetCount ?? 0;
  return n;
}

export function computeSessionResult(
  input: SessionResultInput,
  computedAt: string,
): SessionResult {
  const {
    session,
    setsInSession,
    exerciseCatalog,
    priorHistory,
    profileBodyweightKg,
  } = input;
  const bw = profileBodyweightKg ?? 0;
  // #116: exercises whose bodyweight sets counted zero (no weight on the profile).
  const bodyweightMissing = new Set<string>();

  // Group sets by exercise AND by the physical machine they were logged on
  // (EQ-1-HIST), preserving the input order for deterministic "first set
  // wins" tie-breaks on topSet selection.
  //
  // ⭐ Why the machine is part of the key and not a passenger field: two
  // machines' kilos are not comparable, so folding them into one aggregate
  // produces a top-e1RM and a PR verdict that describe nothing. An entry with
  // no machine named lands under the Default instance (key suffix absent),
  // which is what every set logged before EQ-1-HIST carries — so this changes
  // NOTHING for an untagged history and needs no backfill.
  //
  // ⛔ Volume deliberately does NOT split: see the volumeByExercise push
  // below, which stays keyed by slug so aggregateVolumeByMuscle sums both
  // machines into one muscle bucket (next/equipment-model.md §8.9).
  const setsByExercise = new Map<string, { sets: LogSet[]; slug: string; instance: string | undefined }>();
  for (const set of setsInSession) {
    const instance = readTagValue(set.tags, EQUIPMENT_INSTANCE_NS);
    const key = loadTrackKey(set.exerciseSlug, instance);
    const bucket = setsByExercise.get(key);
    if (bucket) bucket.sets.push(set);
    else setsByExercise.set(key, { sets: [set], slug: set.exerciseSlug, instance });
  }

  // Per-exercise rolled-up volume; consumed by aggregateVolumeByMuscle
  // after the per-exercise loop closes. Keeps the helper pure (it sees
  // only finished totals, not per-set details) and matches the contract
  // the Logger live totals + share card use.
  const volumeByExercise: VolumeBySetInput[] = [];
  const exerciseResults: ExerciseResult[] = [];

  for (const { sets, slug, instance } of setsByExercise.values()) {
    const catalogEntry = exerciseCatalog.get(slug);

    // Per-exercise aggregates.
    let totalVolumeKg = 0;
    let tutSec = 0;
    let impulseKgS = 0;
    let setCount = 0;
    // Effort-filtered subset of setCount — sets at RIR ≤ EFFORT_HARD_SET_RIR
    // (the AP-2 hard-set accumulator). Needs per-set RIR, which only exists
    // here at compute time (the SessionResult aggregate keeps just topSetRir).
    let hardSetCount = 0;
    let topE1rmKg: number | null = null;
    let topSet: { weightKg: number; reps: number } | null = null;
    // Effective RIR of the set that produced topE1rmKg — resolved through the
    // canonical predicate (fail ⇒ 0; rir ?? rpeToRir(rpe); null only when truly
    // untagged). Was `set.rir ?? null`, which contradicted this comment (a fail
    // reported null) and dropped rpe-only rows from every regression tier (MX-3).
    let topSetRir: number | null = null;
    let heaviestWeightedSet: { weightKg: number; reps: number } | null = null;

    for (const set of sets) {
      const m = setMetrics(set, catalogEntry, bw);
      if (bw <= 0 && setLoadsBodyweight(set, catalogEntry)) bodyweightMissing.add(slug);
      if (m.volumeKg > 0) totalVolumeKg += m.volumeKg;
      tutSec += m.tutSec;
      impulseKgS += m.impulseKgS;
      const counts = countsFromMetrics(m);
      if (counts) setCount += 1;
      // A hard set is a counted set whose EFFECTIVE effort is at/below the
      // working-set RIR threshold — a fail resolves to RIR 0 (taken to
      // failure), a set with no fail and no RIR is effort-unknown and is
      // never assumed hard. effectiveRir is the single fail⇒RIR-0 predicate.
      // ⛔ The threshold is EFFORT_HARD_SET_RIR, not the e1RM-trust one: an
      // easy set still trains, it just can't prove a ceiling (MSC-2).
      const eff = effectiveRir(set);
      if (counts && eff != null && eff <= EFFORT_HARD_SET_RIR) {
        hardSetCount += 1;
      }
      // Prefer the row's stamped e1rmKg (server-stamped at write time
      // since shared 1.77.0) but fall back to recomputing for legacy
      // rows that pre-date the field. computeE1rmKg returns null for
      // ineligible sets, which is what we want.
      const e1rm = set.e1rmKg ?? computeE1rmKg(set);
      if (e1rm !== null && e1rm !== undefined) {
        if (topE1rmKg === null || e1rm > topE1rmKg) {
          topE1rmKg = e1rm;
          if (set.reps !== undefined && set.weight !== undefined) {
            topSet = { weightKg: set.weight, reps: set.reps };
          }
          topSetRir = effectiveRir(set);
        }
      }
      // Track the heaviest weighted set independently so we can fall
      // back to it when no set was e1RM-eligible (e.g. all sets were
      // failed but the user still wants to see "what was the heaviest
      // attempt").
      if (
        set.kind === "weighted" &&
        set.weight !== undefined &&
        set.reps !== undefined &&
        set.weight > 0
      ) {
        if (
          heaviestWeightedSet === null ||
          set.weight > heaviestWeightedSet.weightKg
        ) {
          heaviestWeightedSet = { weightKg: set.weight, reps: set.reps };
        }
      }
    }

    if (topSet === null && heaviestWeightedSet !== null) {
      topSet = heaviestWeightedSet;
    }

    // Stash per-exercise total volume for the aggregator. The helper
    // distributes via Exercise.muscleContrib (slice #4 — no more
    // double-counting on multi-primary lifts, no more primary[0]
    // sub-counting on the Logger).
    if (catalogEntry && totalVolumeKg > 0) {
      volumeByExercise.push({ exerciseId: slug, volumeKg: totalVolumeKg });
    }

    // PR + delta against the priors for this exercise ON THIS MACHINE.
    // ⛔ Keyed the same way as the grouping above on purpose: looking the
    // priors up by slug alone would compare a lift on one machine against a
    // record set on another, which is the exact comparison EQ-1-HIST exists
    // to stop making.
    const priors = priorHistory.get(loadTrackKey(slug, instance)) ?? [];
    const vsPriors = compareWithPriors(
      { topE1rmKg, totalVolumeKg, setCount, topSetReps: topSet?.reps ?? null },
      priors,
    );

    exerciseResults.push({
      exerciseSlug: slug,
      // EQ-1-HIST — absent means the Default instance, never "unknown".
      ...(instance ? { equipmentInstance: instance } : {}),
      // Snapshot dropped per
      // [[feedback_no_denormalized_display_snapshots]] — clients
      // resolve via the live catalog using exerciseSlug. The schema
      // field stays optional for legacy rows.
      topE1rmKg,
      topSet,
      topSetRir,
      totalVolumeKg,
      tutSec,
      impulseKgS,
      setCount,
      hardSetCount,
      ...vsPriors,
    });
  }

  // Aggregate top-level numbers.
  let totalVolumeKg = 0;
  let totalTutSec = 0;
  let totalImpulseKgS = 0;
  for (const er of exerciseResults) {
    totalVolumeKg += er.totalVolumeKg;
    totalTutSec += er.tutSec;
    totalImpulseKgS += er.impulseKgS;
  }

  const durationMinutes = sessionDurationMin(session.startedAt, session.endedAt);
  const densityKgPerMin =
    durationMinutes !== null && durationMinutes > 0
      ? totalVolumeKg / durationMinutes
      : null;

  // Single aggregator pass over the rolled-up per-exercise volumes.
  // Catalog rows with `muscleContrib` distribute via that map; rows
  // without (cardio + custom rows pre-backfill) fall back to
  // primary[0] @ 1.0 inside the helper.
  const catalogForAggregator = new Map<
    string,
    { muscleContrib?: MuscleContrib; primary?: readonly string[] }
  >();
  for (const [slug, entry] of exerciseCatalog) {
    catalogForAggregator.set(slug, {
      muscleContrib: entry.muscleContrib,
      primary: entry.primary,
    });
  }
  const volumeByMuscleCatalog = aggregateVolumeByMuscle(
    volumeByExercise,
    catalogForAggregator,
  );
  const volumeByMuscleFinal: VolumeByMuscle = {};
  for (const [slug, v] of Object.entries(volumeByMuscleCatalog)) {
    if (typeof v === "number" && v > 0) {
      volumeByMuscleFinal[slug] = v;
    }
  }

  const sessionLabel = deriveSessionLabel(volumeByMuscleCatalog);

  return {
    sessionId: session.id,
    userSub: session.userSub,
    computedAt,
    totalVolumeKg,
    totalTutSec,
    totalImpulseKgS,
    durationMinutes,
    densityKgPerMin,
    sessionLabel,
    volumeByMuscle: volumeByMuscleFinal,
    exerciseResults,
    ...(bodyweightMissing.size > 0 ? { bodyweightMissing: [...bodyweightMissing].sort() } : {}),
  };
}
